import { useEffect, useMemo, useState } from "react";
import { Model_AssetStoreGet, Model_ConfiguratorProductGet, Model_ProductGet, Model_ProductPictureGet, Shared_AssetKinds, Shared_Languages, Shared_PictureResolutions } from "src/backend/openapi/v1";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/store";
import { productDetailsActions } from "./product-details-store";
import { useLocation } from "react-router";
import { useUserSettings } from "src/hooks";

type Props = {
  productId?: string;
};

export const useProductInfo = (props: Props) => {
  const { productId } = props;
  const [pictures, setPictures] = useState<Model_ProductPictureGet[]>([]);
  const [loadedPictureIds, setLoadedPictureIds] = useState<string[]>([]);
  const [product, setProduct] = useState<Model_ProductGet & Model_ConfiguratorProductGet>();
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const location = useLocation();

  const dispatch = useDispatch();
  const { productState } = useSelector((state: RootState) => state.pageReducer.productDetailsReducer);

  const { language } = useUserSettings();

  useEffect(() => {
    let languagePayload: Shared_Languages = Shared_Languages.English;
    switch (language) {
      case "en":
        languagePayload = Shared_Languages.English;
        break;
      case "da":
        languagePayload = Shared_Languages.Danish;
    }
    if (location.state?.product) {
      setProduct(location.state?.product);
    } else if (productId) dispatch(productDetailsActions.getProductByProductIdRequest(productId, languagePayload));
    return () => {
      setProduct(undefined);
    };
  }, [productId, language]);

  useEffect(() => {
    return () => {
      dispatch(productDetailsActions.getProductByProductIdReset());
    };
  }, []);

  useEffect(() => {
    if (productState.loading === "has-loaded" && productState.data.productId === productId) {
      setProduct(productState.data as Model_ProductGet & Model_ConfiguratorProductGet);
    }
  }, [productState]);

  // Get all medium size pictures for current product
  useEffect(() => {
    if (productId) {
      setIsLoadingImages(true);
      dispatch(
        productDetailsActions.getProductPicturesRequest(productId, Shared_PictureResolutions.Medium, (isSuccess, data) => {
          if (isSuccess && data) {
            setPictures(data.sort((x, y) => x.sortOrder - y.sortOrder));
          }
          setIsLoadingImages(false);
        })
      );
    }
  }, [productId]);

  // Get 4 large pictures which are showing
  useEffect(() => {
    if (pictures.length > 0) {
      const pictureIds = pictures
        .slice(0, 4)
        .map(picture => picture.productPictureId)
        .filter(x => x);
      setLoadedPictureIds(prev => [...prev, ...pictureIds]);
      dispatch(
        productDetailsActions.getMultipleProductPicturesRequest(pictureIds, Shared_PictureResolutions.Large, (isSuccess, data) => {
          if (isSuccess && data) {
            setPictures(prev => {
              return prev.map(picture => {
                const loadedPicture = data.find(item => item.productPictureId === picture.productPictureId);
                return { ...picture, picture: loadedPicture?.picture ?? picture.picture };
              });
            });
          }
        })
      );
    }
  }, [pictures.length]);

  // Get images as user goes to next or previous pictures
  useEffect(() => {
    if (pictures.length > 0 && currentIndex > -1) {
      // get indexes of pictures to load. get 5 previous and 5 next pictures from current index
      const numbers = [currentIndex];
      for (let i = 1; i <= 5; i++) {
        numbers.push(currentIndex + i);
        numbers.unshift(currentIndex - i);
      }

      // get picture ids which isn't already loaded
      const pictureIds = numbers.map(index => pictures?.[index]?.productPictureId).filter(x => x && !loadedPictureIds.includes(x));
      const loadRestImages = pictures.length - loadedPictureIds.length === pictureIds.length && pictureIds.length > 0;

      //1. get pictures only if it needs to load 5 pictures. added condition so it does not call api for every single image
      //2. if there's less than 5 images remaining then bypass condition using loadRestImages variable
      if (pictureIds.length > 4 || loadRestImages) {
        setLoadedPictureIds(prev => [...prev, ...pictureIds]);
        dispatch(
          productDetailsActions.getMultipleProductPicturesRequest(pictureIds, Shared_PictureResolutions.Large, (isSuccess, data) => {
            if (isSuccess && data) {
              setPictures(prev => {
                return prev.map(picture => {
                  // find and update medium size picture with large size picture
                  const loadedPicture = data.find(item => item.productPictureId === picture.productPictureId);
                  return { ...picture, picture: loadedPicture?.picture ?? picture.picture };
                });
              });
            }
          })
        );
      }
    }
  }, [currentIndex]);

  const primaryPicture = pictures.find(picture => picture.isPrimary);

  const assetStoreData = useMemo(() => {
    const data: Record<string, Model_AssetStoreGet[]> = {
      text: [],
      links: [],
      documents: []
    };
    if (product && "assetStores" in product) {
      data["text"] = product?.assetStores?.filter(assetStore => assetStore.assetKindId === Shared_AssetKinds.Text) || [];
      data["links"] = product?.assetStores?.filter(assetStore => assetStore.assetKindId === Shared_AssetKinds.Link) || [];
      data["documents"] = product?.assetStores?.filter(assetStore => assetStore.assetKindId === Shared_AssetKinds.Document) || [];
    }
    return data;
  }, [product]);

  const groupedAssetStoreDocumentsByType = assetStoreData.documents.reduce(
    (acc, assetStoreItem) => {
      const index = assetStoreItem?.documentType?.sortOrder || Number.MAX_SAFE_INTEGER;
      if (acc[index]) {
        acc[index].push(assetStoreItem);
      } else {
        acc[index] = [assetStoreItem];
      }

      return acc;
    },
    {} as Record<number, typeof assetStoreData.documents>
  );

  return {
    assetStoreData,
    primaryPicture,
    isLoadingImages,
    pictures,
    product,
    currentIndex,
    setCurrentIndex,
    groupedAssetStoreDocumentsByType
  };
};
